import React, {
    createContext,
    useContext,
    useState,
    useEffect,
    useCallback,
} from "react";
import { User, AuthContextType } from "../types";
import { authService, adminAuthService } from "../services/api";

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = (): AuthContextType => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error("useAuth must be used within an AuthProvider");
    }
    return context;
};

interface AuthProviderProps {
    children: React.ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
    const [user, setUser] = useState<User | null>(null);
    const [token, setToken] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(true);

    // Charger l'utilisateur depuis le localStorage au démarrage
    useEffect(() => {
        const storedToken = localStorage.getItem("token");
        const storedUser = localStorage.getItem("user");

        if (storedToken && storedUser) {
            setToken(storedToken);
            setUser(JSON.parse(storedUser));
        }
        setIsLoading(false);
    }, []);

    const login = useCallback(
        async (
            username: string,
            password: string,
            isAdmin: boolean = false,
        ) => {
            setIsLoading(true);
            try {
                const response = isAdmin
                    ? await adminAuthService.login(username, password)
                    : await authService.login(username, password);

                const userData: User = {
                    id: response.user.id,
                    username: response.user.username,
                    role: response.user.role as "client" | "admin",
                    is_approved: response.user.is_approved,
                };

                setToken(response.access_token);
                setUser(userData);

                localStorage.setItem("token", response.access_token);
                localStorage.setItem("user", JSON.stringify(userData));
            } finally {
                setIsLoading(false);
            }
        },
        [],
    );

    const register = useCallback(async (formData: FormData) => {
        setIsLoading(true);
        try {
            const response = await authService.register(formData);

            const userData: User = {
                id: response.user.id,
                username: response.user.username,
                role: response.user.role as "client" | "admin",
                is_approved: response.user.is_approved,
            };

            setToken(response.access_token);
            setUser(userData);

            localStorage.setItem("token", response.access_token);
            localStorage.setItem("user", JSON.stringify(userData));
        } finally {
            setIsLoading(false);
        }
    }, []);

    const logout = useCallback(async () => {
        try {
            if (user?.role === "admin") {
                await adminAuthService.logout();
            } else {
                await authService.logout();
            }
        } catch (error) {
            // Ignorer les erreurs de logout
        } finally {
            setToken(null);
            setUser(null);
            localStorage.removeItem("token");
            localStorage.removeItem("user");
        }
    }, [user]);

    const updateUser = useCallback((updatedUser: User) => {
        setUser(updatedUser);
        localStorage.setItem("user", JSON.stringify(updatedUser));
    }, []);

    const isApproved = user?.role === "admin" || user?.is_approved === true;

    const value: AuthContextType = {
        user,
        token,
        isAuthenticated: !!token && !!user,
        isAdmin: user?.role === "admin",
        isApproved,
        isLoading,
        login,
        register,
        logout,
        updateUser,
    };

    return (
        <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
    );
};

export default AuthContext;
